// Payments API base URL — injected at build time via VITE_PAYMENTS_URL.
// Falls back to empty string in dev so calls 404 loudly instead of going somewhere unexpected.
const PAYMENTS_URL: string = import.meta.env.VITE_PAYMENTS_URL ?? '';

// Auth API base URL — used by the magic-link login pages (POST endpoints
// the frontend calls directly, separate from the OAuth-helper /authorize
// and /token redirects in lib/auth.ts).
const AUTH_URL: string = import.meta.env.VITE_AUTH_URL ?? '';

/** Subscription plan identifiers — must match the allowlist in
 *  CreateCheckoutSessionHandler.java's `resolvePriceId`. */
export type Plan = 'monthly' | 'yearly';

/** Asks the backend for a fresh Stripe Checkout Session URL to redirect the browser to.
 *
 * Sends our current origin as the success/cancel base so localhost dev redirects
 * back to localhost, prod redirects to thedraftlion.com. The backend allowlists
 * which origins it'll accept, so this can't be abused.
 *
 * `plan` defaults to 'yearly' for back-compat with anything that still
 * calls this without args. New callers should pass it explicitly.
 */
export async function createCheckoutSession(
  plan: Plan = 'yearly',
): Promise<{ url: string; session_id: string }> {
  const origin = window.location.origin; // e.g. "http://localhost:5173" or "https://thedraftlion.com"
  const res = await fetch(`${PAYMENTS_URL}/create-checkout-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      plan,
      successUrl: `${origin}/success`,
      cancelUrl: `${origin}/cancel`,
    }),
  });
  if (!res.ok) throw new Error(`Checkout failed (${res.status})`);
  return res.json();
}

/** Asks the backend for a one-time Stripe Billing Portal URL.
 *
 *  The backend pulls the email from the validated Cognito access token,
 *  looks up the Stripe customer ID in the SubscriptionsTable, and returns
 *  a Billing Portal session URL the browser can navigate to.
 */
export async function createPortalSession(accessToken: string): Promise<{ url: string }> {
  const res = await fetch(`${PAYMENTS_URL}/create-portal-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) {
    let msg = `Portal session failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

// ------------------------------------------------------------------
// Magic-link login endpoints (called by /login and /login/complete pages)
// ------------------------------------------------------------------

/** POST /oauth/login/start — fires a magic-link email to the user.
 *
 *  Always resolves with `{status: "sent"}` regardless of whether the
 *  email matched a real user, to avoid leaking subscription status.
 */
export async function loginStart(email: string, oauthSession: string): Promise<{ status: string }> {
  const res = await fetch(`${AUTH_URL}/oauth/login/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, session: oauthSession }),
  });
  if (!res.ok) {
    let msg = `Login start failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error_description) msg = body.error_description;
      else if (body?.error) msg = body.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

/** POST /oauth/login/complete — verifies the nonce from the magic link and
 *  returns the redirect URL we should send the browser to (back to whoever
 *  initiated the OAuth flow — could be ChatGPT, Claude, or our own site).
 */
export async function loginComplete(linkId: string, nonce: string): Promise<{ redirect: string }> {
  const res = await fetch(`${AUTH_URL}/oauth/login/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ link_id: linkId, t: nonce }),
  });
  if (!res.ok) {
    let msg = `Login complete failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error_description) msg = body.error_description;
      else if (body?.error) msg = body.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}
